/**
 * Pure logic behind the Mongo MCP guardrails panel (McpGuardrailsPanel and
 * its field-picker column), kept out of the components so it can be tested
 * on its own.
 *
 * Two kinds of hidden fields:
 *   - global: hidden on every collection of the database (name, address,
 *     nif...). The backend stores and the gateway applies them; the panel
 *     only reads and writes the list.
 *   - per collection: hidden on one allowed collection only. A field that is
 *     already global is never stored again per collection.
 */
import type { McpCollectionPolicyInput, McpPolicyResponse } from '../types/mcp';

export interface GuardrailsState {
  /** Collections Claude may query. */
  allowed: string[];
  /** Hidden fields of each allowed collection (global ones excluded). */
  hiddenByCollection: Record<string, string[]>;
  /** Hidden on every collection of the database. */
  globalHidden: string[];
}

/** The panel's state for ONE database, out of the policy for every database. */
export function stateFromPolicy(response: McpPolicyResponse, database: string): GuardrailsState {
  const allowed = response.policies.filter((p) => p.database === database && p.allowed);
  return {
    allowed: allowed.map((p) => p.collection),
    hiddenByCollection: Object.fromEntries(allowed.map((p) => [p.collection, p.hiddenFields])),
    globalHidden: response.globalHiddenFields[database] ?? [],
  };
}

/** The PUT body: only allowed collections, without fields that are already global. */
export function buildSavePayload(state: GuardrailsState): {
  policies: McpCollectionPolicyInput[];
  globalHiddenFields: string[];
} {
  const global = new Set(state.globalHidden);
  return {
    policies: state.allowed.map((collection) => ({
      collection,
      allowed: true,
      hiddenFields: (state.hiddenByCollection[collection] ?? []).filter((f) => !global.has(f)),
    })),
    globalHiddenFields: state.globalHidden,
  };
}

/** Drops the hidden fields of collections that are no longer allowed. */
export function keepHiddenForAllowed(
  hiddenByCollection: Record<string, string[]>,
  allowed: readonly string[],
): Record<string, string[]> {
  const kept: Record<string, string[]> = {};
  for (const key of allowed) if (hiddenByCollection[key]) kept[key] = hiddenByCollection[key];
  return kept;
}

/** Trimmed, non-empty, deduplicated field names (order kept). */
export function normalizeFieldNames(values: readonly string[]): string[] {
  return Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)));
}

/** Sampled fields plus hidden ones the sample missed, sorted. */
export function fieldsForCollection(sampled: readonly string[], hidden: readonly string[]): string[] {
  return Array.from(new Set([...sampled, ...hidden])).sort((a, b) => a.localeCompare(b));
}

/** Fields that do reach Claude: neither global nor hidden for this collection. */
export function countAllowedFields(
  fields: readonly string[],
  hidden: readonly string[],
  global: ReadonlySet<string>,
): number {
  return fields.filter((f) => !global.has(f) && !hidden.includes(f)).length;
}

/** Allows (removes from hidden) or hides one field. */
export function setFieldAllowed(hidden: readonly string[], field: string, allowed: boolean): string[] {
  if (allowed) return hidden.filter((f) => f !== field);
  return hidden.includes(field) ? [...hidden] : [...hidden, field];
}

/** Allows or hides several fields at once (the column's "all" checkbox). */
export function setFieldsAllowed(hidden: readonly string[], fields: readonly string[], allowed: boolean): string[] {
  const next = new Set(hidden);
  for (const f of fields) {
    if (allowed) next.delete(f);
    else next.add(f);
  }
  return Array.from(next);
}

/** The key that is in `next` but was not in `previous` (the one just checked), if any. */
export function newlySelected(previous: readonly string[], next: readonly string[]): string | undefined {
  return next.find((k) => !previous.includes(k));
}
